import { useEffect, useState } from "react";
import { adminGrant, adminListUsers, adminUserLedger } from "../../api";
import { useSession } from "../../state";

type AdminUser = {
  cognito_sub: string;
  email: string;
  free_credits: number;
  tokens: number;
  is_admin: boolean;
  accepted_terms_at: string | null;
  job_count: number;
  created_at: string;
};

export function AdminUsers() {
  const { token } = useSession();
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<AdminUser | null>(null);
  const [ledger, setLedger] = useState<any[] | null>(null);

  const load = async () => {
    const t = (await token())!;
    setRows(await adminListUsers(t, q || undefined));
  };
  useEffect(() => { load(); }, []);

  const grant = async (u: AdminUser, dc: number, dt: number) => {
    setBusy(u.cognito_sub);
    try {
      const t = (await token())!;
      await adminGrant(t, u.cognito_sub, { delta_credits: dc, delta_tokens: dt });
      await load();
    } catch (e: any) { alert(e.message); }
    finally { setBusy(null); }
  };

  const openLedger = async (u: AdminUser) => {
    setDrawer(u);
    setLedger(null);
    const t = (await token())!;
    setLedger(await adminUserLedger(t, u.cognito_sub));
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input className="input flex-1" placeholder="search by email or sub" value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()} />
        <button onClick={load} className="btn-ghost text-sm bg-cream-dark px-4">search</button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-mute text-xs uppercase tracking-wider">
            <tr><th>email</th><th>credits</th><th>tokens</th><th>jobs</th><th>terms</th><th>admin</th><th>actions</th></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.cognito_sub} className="border-t border-ink/10">
                <td className="py-2 pr-3">
                  <button onClick={() => openLedger(u)} className="hover:underline text-left">{u.email}</button>
                  <div className="text-[10px] text-ink-mute">{u.cognito_sub.slice(0,8)}…</div>
                </td>
                <td className="pr-3">{u.free_credits}</td>
                <td className="pr-3">{u.tokens}</td>
                <td className="pr-3">{u.job_count}</td>
                <td className="pr-3 text-xs">{u.accepted_terms_at ? "✓" : "—"}</td>
                <td className="pr-3 text-xs">{u.is_admin ? "✓" : "—"}</td>
                <td className="py-2">
                  <button onClick={() => grant(u, 1, 0)} disabled={busy===u.cognito_sub}
                          className="btn-ghost text-xs px-2 py-1 bg-cream-dark mr-1">+1 credit</button>
                  <button onClick={() => grant(u, 0, 1)} disabled={busy===u.cognito_sub}
                          className="btn-ghost text-xs px-2 py-1 bg-cream-dark mr-1">+1 token</button>
                  <button onClick={() => grant(u, 0, 5)} disabled={busy===u.cognito_sub}
                          className="btn-ghost text-xs px-2 py-1 bg-cream-dark">+5 tokens</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drawer && (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40 grid place-items-center px-4" onClick={() => setDrawer(null)}>
          <div className="bg-cream rounded-3xl shadow-soft p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black text-xl mb-3">{drawer.email}</h3>
            <p className="text-xs text-ink-mute mb-4">credits: {drawer.free_credits} · tokens: {drawer.tokens}</p>
            {ledger === null ? <p>loading…</p> : (
              <table className="w-full text-xs">
                <thead><tr className="text-ink-mute"><th className="text-left">when</th><th className="text-left">Δcredits</th><th className="text-left">Δtokens</th><th className="text-left">reason</th></tr></thead>
                <tbody>
                  {ledger.map((e) => (
                    <tr key={e.id} className="border-t border-ink/10">
                      <td className="py-1 pr-2">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="pr-2">{e.delta_credits}</td>
                      <td className="pr-2">{e.delta_tokens}</td>
                      <td className="pr-2">{e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button onClick={() => setDrawer(null)} className="btn-ghost mt-4 text-sm bg-cream-dark">close</button>
          </div>
        </div>
      )}
    </div>
  );
}
